import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import styled from '@emotion/styled';
import { getDevices, getFleetStats, getFleetStatsByCompany } from '../services/devices';
import { getMqttStatus } from '../services/companies';
import { getAlertHistory, acknowledgeAlert, resolveAlert } from '../services/alerts';
import type { Alert } from '../services/alerts';
import { LinkButton as AlertActionButton } from '../components/styled';
import { getMrrHistory, getAllBilling } from '../services/billing';
import { getGateways } from '../services/gateways';
import type { Gateway } from '../services/gateways';
import { useAuth } from '../context/AuthContext';
import { isCompanyAdmin, isSuperAdmin } from '../services/users';
import {
  PageContainer,
  PageHeader,
  PageTitle,
  StatsGrid,
  StatCard,
  DetailCard,
  TableContainer,
  StyledTable,
  Th,
  Td,
  StatusChip,
  EmptyState,
  AlertBanner,
  LoadingText,
  MutedText,
  InlineCode,
} from '../components/styled';
import isPropValid from '@emotion/is-prop-valid';

// Compact relative time for "last seen" / alert timestamps. The fleet refetches
// on a 30s interval so minute-granularity is plenty.
function timeAgo(dateStr?: string | null): string {
  if (!dateStr) return 'Never';
  const ms = Date.now() - new Date(dateStr).getTime();
  if (Number.isNaN(ms)) return 'Never';
  const mins = Math.floor(ms / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

// Alert severity → StatusChip semantic variant.
const severityVariant = (s: Alert['severity']) =>
  s === 'critical' ? 'critical' as const
  : s === 'warning' ? 'warning' as const
  : 'info' as const;

// Match the Billing page's money formatting ($1,234.56).
const formatCurrency = (value?: number) =>
  `$${Number(value || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Fleet online% → health chip. >75 healthy, >50 needs-watching, else critical.
const healthChipVariant = (pct: number | null) =>
  pct === null ? 'neutral' as const
  : pct > 75 ? 'positive' as const
  : pct > 50 ? 'warning' as const
  : 'critical' as const;

// Gateway status → chip variant. Healthy = active/online.
const gatewayVariant = (s: Gateway['status']) =>
  s === 'online' || s === 'active' ? 'positive' as const
  : s === 'error' || s === 'offline' ? 'critical' as const
  : 'warning' as const;

const ChartsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: ${({ theme }) => theme.spacing(4)};
  margin-bottom: ${({ theme }) => theme.spacing(5)};
`;

const MqttStatusRow = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing(4)};
`;

const MqttDot = styled('div', {
  shouldForwardProp: prop => isPropValid(prop) && prop !== 'online',
})<{ online: boolean }>`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  margin-right: ${({ theme }) => theme.spacing(3)};
  background: ${({ theme, online }) => online ? theme.color.green : theme.color.red};
`;

const MqttStatusLabel = styled.span`
  font-size: ${({ theme }) => theme.font.size.xl};
  font-weight: ${({ theme }) => theme.font.weight.semiBold};
  color: ${({ theme }) => theme.font.color.primary};
`;

const QuickLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  height: 32px;
  padding: 0 12px;
  font-size: ${({ theme }) => theme.font.size.sm};
  font-weight: ${({ theme }) => theme.font.weight.medium};
  color: ${({ theme }) => theme.font.color.primary};
  background: ${({ theme }) => theme.background.secondary};
  border: 1px solid ${({ theme }) => theme.border.color.medium};
  border-radius: ${({ theme }) => theme.border.radius.sm};
  text-decoration: none;
  transition: background 0.1s ease;

  &:hover {
    background: ${({ theme }) => theme.background.tertiary};
  }
`;

const DeviceLink = styled(Link)`
  color: ${({ theme }) => theme.accent.primary};
  text-decoration: none;
  font-weight: ${({ theme }) => theme.font.weight.medium};

  &:hover {
    text-decoration: underline;
  }
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${({ theme }) => theme.spacing(4)};
  /* Both uses follow a card row that adds no bottom margin of its own. */
  margin-top: ${({ theme }) => theme.spacing(8)};

  h2 {
    font-size: ${({ theme }) => theme.font.roles.sectionH2.size};
    font-weight: ${({ theme }) => theme.font.roles.sectionH2.weight};
    color: ${({ theme }) => theme.font.color.primary};
    margin: 0;
  }
`;

// Centered footer cell — used for the "Configure MQTT" link in the empty state.
const HealthCardFooter = styled.div`
  text-align: center;
  margin-top: ${({ theme }) => theme.spacing(4)};
`;

// "View Devices" hint shown next to the alert banner heading.
const AlertHint = styled(MutedText)`
  margin-left: ${({ theme }) => theme.spacing(2)};
`;

// Numeric metric inside an MQTT-status dl (Tracked Devices count).
const MetricValue = styled.dd`
  font-size: ${({ theme }) => theme.font.size.lg};
  font-weight: ${({ theme }) => theme.font.weight.semiBold};
  color: ${({ theme }) => theme.font.color.primary};
`;

// Error message inside an MQTT-status dl.
const ErrorValue = styled.dd`
  color: ${({ theme }) => theme.color.red};
`;

// Recent-alerts feed inside the DetailCard.
const FeedList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
`;

const FeedItem = styled.li`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(3)};
  padding: ${({ theme }) => theme.spacing(3)} 0;
  border-bottom: 1px solid ${({ theme }) => theme.border.color.light};

  &:last-of-type {
    border-bottom: none;
  }
`;

const FeedMain = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1)};
  min-width: 0;
`;

const FeedTitle = styled.span`
  font-size: ${({ theme }) => theme.font.size.sm};
  font-weight: ${({ theme }) => theme.font.weight.medium};
  color: ${({ theme }) => theme.font.color.primary};
`;

const FeedMeta = styled.span`
  font-size: ${({ theme }) => theme.font.size.xs};
  color: ${({ theme }) => theme.font.color.tertiary};
`;

// MRR trend hint inside a StatCard's <small>. Green for growth, red for decline.
const TrendUp = styled.span`
  color: ${({ theme }) => theme.color.green};
`;

const TrendDown = styled.span`
  color: ${({ theme }) => theme.color.red};
`;

// Right-aligned numeric / money cell in the per-company table.
const NumCell = styled(Td)`
  text-align: right;
  font-feature-settings: "tnum";
  font-variant-numeric: tabular-nums;
`;

const NumHead = styled(Th)`
  text-align: right;
`;

const CompanyLink = styled(Link)`
  color: ${({ theme }) => theme.accent.primary};
  text-decoration: none;
  font-weight: ${({ theme }) => theme.font.weight.medium};

  &:hover {
    text-decoration: underline;
  }
`;

function Dashboard() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const userIsCompanyAdmin = isCompanyAdmin(user);

  // Alert lifecycle actions on the feed. Both refetch the feed so the status
  // line and the active-alert banner update immediately.
  const refreshAlerts = () => queryClient.invalidateQueries({ queryKey: ['recent-alerts'] } as any);
  const ackMutation = useMutation({ mutationFn: acknowledgeAlert, onSuccess: refreshAlerts });
  const resolveMutation = useMutation({ mutationFn: resolveAlert, onSuccess: refreshAlerts });
  const userIsSuperAdmin = isSuperAdmin(user);
  // The operator view (needs-attention + alerts) is for OEM customers running
  // their own fleet. Super admins keep the platform-wide layout below.
  const showOperatorView = !userIsSuperAdmin;

  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['fleet-stats'],
    queryFn: getFleetStats,
    refetchInterval: 30000,
  });

  // Deployed devices that aren't reporting in — the fleet's actual problem set.
  // Stalest (oldest last_seen) first so the worst offenders are at the top.
  // `.total` is the authoritative "needs attention" count; the rows are a preview.
  const { data: attentionData, isLoading: attentionLoading } = useQuery({
    queryKey: ['attention-devices'],
    queryFn: () => getDevices({ status: 'offline', deployed: true, sort: 'last_seen', order: 'asc', limit: 8 }),
    enabled: showOperatorView,
    refetchInterval: 30000,
  });

  const { data: mqttStatus, isLoading: mqttLoading } = useQuery({
    queryKey: ['mqtt-status', user?.company_id],
    queryFn: () => getMqttStatus(user?.company_id),
    enabled: userIsCompanyAdmin && !!user?.company_id,
    refetchInterval: 30000,
  });

  const { data: alertsData } = useQuery({
    queryKey: ['recent-alerts'],
    queryFn: () => getAlertHistory({ limit: 5 }),
    enabled: userIsCompanyAdmin,
    refetchInterval: 60000,
  });

  // --- Super-admin platform queries ---
  const { data: companyStats, isLoading: companyStatsLoading } = useQuery({
    queryKey: ['fleet-by-company'],
    queryFn: getFleetStatsByCompany,
    enabled: userIsSuperAdmin,
    refetchInterval: 60000,
  });

  const { data: mrrHistory } = useQuery({
    queryKey: ['mrr-history'],
    queryFn: () => getMrrHistory(12),
    enabled: userIsSuperAdmin,
  });

  const { data: allBilling } = useQuery({
    queryKey: ['all-billing'],
    queryFn: getAllBilling,
    enabled: userIsSuperAdmin,
  });

  const { data: gatewaysData } = useQuery({
    queryKey: ['gateways'],
    queryFn: () => getGateways(),
    enabled: userIsSuperAdmin,
    refetchInterval: 60000,
  });

  const recentAlerts = alertsData?.alerts || [];
  const activeAlerts = recentAlerts.filter(a => a.status === 'active');
  const attentionDevices = attentionData?.devices || [];
  const attentionCount = attentionData?.total || 0;

  const statusVariant = (s: string) => (s === 'online' ? 'positive' as const : 'critical' as const);

  // --- Super-admin derived data ---
  // NOTE: the billing service return TYPES are inaccurate — /billing/all really
  // returns { companies, totals } and /billing/mrr-history returns { history }.
  // Read the real shape via a localized cast rather than trust the declared types.
  const billingResp = allBilling as unknown as {
    companies?: Array<{ id: number; monthlyTotal?: number }>;
    totals?: { totalMonthly?: number };
  } | undefined;
  const mrrResp = mrrHistory as unknown as { history?: Array<{ mrr: number }> } | undefined;

  const currentMrr = billingResp?.totals?.totalMonthly ?? 0;
  const mrrSeries = mrrResp?.history ?? [];
  const prevMrr = mrrSeries.length > 1 ? mrrSeries[mrrSeries.length - 2].mrr : 0;
  const mrrTrendPct = prevMrr > 0 ? Math.round(((currentMrr - prevMrr) / prevMrr) * 100) : null;

  const mrrByCompany = new Map((billingResp?.companies ?? []).map(b => [b.id, b.monthlyTotal ?? 0]));
  // One row per company: fleet health joined with monthly revenue. Worst fleet
  // health first (companies with no devices sink to the bottom).
  const companyRows = (companyStats || [])
    .map(c => {
      const onlinePct = c.stats.total > 0 ? Math.round((c.stats.online / c.stats.total) * 100) : null;
      return {
        id: c.company_id,
        name: c.company_name,
        total: c.stats.total,
        online: c.stats.online,
        offline: c.stats.offline,
        onlinePct,
        mrr: mrrByCompany.get(c.company_id) ?? 0,
      };
    })
    .sort((a, b) => {
      if (a.onlinePct === null) return b.onlinePct === null ? 0 : 1;
      if (b.onlinePct === null) return -1;
      return a.onlinePct - b.onlinePct;
    });

  const companyCount = companyRows.length;
  const companyNameById = new Map(companyRows.map(c => [c.id, c.name]));

  const gateways = gatewaysData?.gateways || [];
  const unhealthyGateways = gateways.filter(g => g.status !== 'online' && g.status !== 'active');

  return (
    <PageContainer>
      <PageHeader>
        <PageTitle>Dashboard</PageTitle>
        {user?.company_name && !userIsSuperAdmin && (
          <MutedText>{user.company_name}</MutedText>
        )}
      </PageHeader>

      {/* Alert Banner */}
      {userIsCompanyAdmin && activeAlerts.length > 0 && (
        <AlertBanner variant="error">
          <div>
            <strong>
              {activeAlerts.length} Active Alert{activeAlerts.length > 1 ? 's' : ''}
            </strong>
            <AlertHint>
              {activeAlerts[0]?.title || activeAlerts[0]?.message || 'Service requires attention'}
            </AlertHint>
          </div>
          <QuickLink to="/devices">View Devices</QuickLink>
        </AlertBanner>
      )}

      {/* Stats Grid */}
      <StatsGrid>
        {userIsSuperAdmin ? (
          <>
            <StatCard>
              <h3>Software Providers</h3>
              <p className="stat-value">{companyStatsLoading ? '...' : companyCount}</p>
              <small>active accounts</small>
            </StatCard>
            <StatCard>
              <h3>Total Devices</h3>
              <p className="stat-value">{statsLoading ? '...' : stats?.total || 0}</p>
              <small>{stats?.deployed || 0} deployed</small>
            </StatCard>
            <StatCard variant="success">
              <h3>Online</h3>
              <p className="stat-value">{statsLoading ? '...' : stats?.online || 0}</p>
              <small>
                {stats?.total ? Math.round((stats.online / stats.total) * 100) : 0}% of fleet
              </small>
            </StatCard>
            <StatCard>
              <h3>MRR</h3>
              <p className="stat-value">{formatCurrency(currentMrr)}</p>
              <small>
                {mrrTrendPct === null ? 'monthly recurring'
                  : mrrTrendPct >= 0 ? <TrendUp>▲ {mrrTrendPct}% vs last month</TrendUp>
                  : <TrendDown>▼ {Math.abs(mrrTrendPct)}% vs last month</TrendDown>}
              </small>
            </StatCard>
          </>
        ) : (
          <>
            <StatCard>
              <h3>Total Devices</h3>
              <p className="stat-value">{statsLoading ? '...' : stats?.total || 0}</p>
              <small>{stats?.deployed || 0} deployed</small>
            </StatCard>
            <StatCard variant="success">
              <h3>Online</h3>
              <p className="stat-value">{statsLoading ? '...' : stats?.online || 0}</p>
              <small>
                {stats?.total ? Math.round((stats.online / stats.total) * 100) : 0}% of fleet
              </small>
            </StatCard>
            <StatCard variant="error">
              <h3>Offline</h3>
              <p className="stat-value">{statsLoading ? '...' : stats?.offline || 0}</p>
              <small>
                {stats?.total ? Math.round((stats.offline / stats.total) * 100) : 0}% of fleet
              </small>
            </StatCard>
            <StatCard variant={attentionCount > 0 ? 'warning' : 'default'}>
              <h3>Needs Attention</h3>
              <p className="stat-value">{attentionLoading ? '...' : attentionCount}</p>
              <small>deployed &amp; offline</small>
            </StatCard>
          </>
        )}
      </StatsGrid>

      {/* Operator view: recent alerts + MQTT status (company admins only) */}
      {showOperatorView && userIsCompanyAdmin && (
        <ChartsRow>
          <DetailCard>
            <h2>Recent Alerts</h2>
            {recentAlerts.length === 0 ? (
              <EmptyState>
                <p>No recent alerts</p>
              </EmptyState>
            ) : (
              <FeedList>
                {recentAlerts.map(alert => (
                  <FeedItem key={alert.id}>
                    <FeedMain>
                      <FeedTitle>{alert.title || alert.message}</FeedTitle>
                      <FeedMeta>{timeAgo(alert.created_at)} · {alert.status}</FeedMeta>
                      {alert.status !== 'resolved' && (
                        <FeedMeta>
                          {alert.status === 'active' && (
                            <>
                              <AlertActionButton
                                onClick={() => ackMutation.mutate(alert.id)}
                                disabled={ackMutation.isPending}
                              >
                                Acknowledge
                              </AlertActionButton>
                              {' · '}
                            </>
                          )}
                          <AlertActionButton
                            onClick={() => resolveMutation.mutate(alert.id)}
                            disabled={resolveMutation.isPending}
                          >
                            Resolve
                          </AlertActionButton>
                        </FeedMeta>
                      )}
                    </FeedMain>
                    <StatusChip variant={severityVariant(alert.severity)}>
                      {alert.severity}
                    </StatusChip>
                  </FeedItem>
                ))}
              </FeedList>
            )}
          </DetailCard>

          <DetailCard>
            <h2>MQTT Service Status</h2>
            {mqttLoading ? (
              <LoadingText>Checking connection...</LoadingText>
            ) : !mqttStatus?.configured ? (
              <EmptyState>
                <p>MQTT service not configured</p>
                <HealthCardFooter>
                  <QuickLink to={`/software-providers/${user?.company_id}?tab=settings`}>
                    Configure MQTT
                  </QuickLink>
                </HealthCardFooter>
              </EmptyState>
            ) : (
              <div>
                <MqttStatusRow>
                  <MqttDot online={mqttStatus.status === 'online'} />
                  <MqttStatusLabel>
                    {mqttStatus.status === 'online' ? 'Connected' : 'Disconnected'}
                  </MqttStatusLabel>
                </MqttStatusRow>
                <dl>
                  <dt>Service URL</dt>
                  <dd><InlineCode>{mqttStatus.serviceUrl}</InlineCode></dd>
                  {mqttStatus.status === 'online' && mqttStatus.health?.components?.registry && (
                    <>
                      <dt>Tracked Devices</dt>
                      <MetricValue>
                        {mqttStatus.health.components.registry.totalDevices || 0}
                      </MetricValue>
                    </>
                  )}
                  {mqttStatus.status === 'offline' && mqttStatus.error && (
                    <>
                      <dt>Error</dt>
                      <ErrorValue>{mqttStatus.error}</ErrorValue>
                    </>
                  )}
                </dl>
              </div>
            )}
          </DetailCard>
        </ChartsRow>
      )}

      {/* Operator view: devices needing attention (replaces "recent devices") */}
      {showOperatorView ? (
        <>
          <SectionHeader>
            <h2>Devices Needing Attention</h2>
            <QuickLink to="/devices">View All Devices</QuickLink>
          </SectionHeader>

          {attentionLoading ? (
            <LoadingText>Loading...</LoadingText>
          ) : attentionDevices.length === 0 ? (
            <EmptyState>
              <p>All deployed devices are reporting in.</p>
            </EmptyState>
          ) : (
            <TableContainer>
              <StyledTable>
                <thead>
                  <tr>
                    <Th>Name</Th>
                    <Th>Device ID</Th>
                    <Th>Status</Th>
                    <Th>Last Seen</Th>
                  </tr>
                </thead>
                <tbody>
                  {attentionDevices.map(device => (
                    <tr key={device.id}>
                      <Td>
                        <DeviceLink to={`/devices/${device.id}`}>
                          {device.name || 'Unnamed'}
                        </DeviceLink>
                      </Td>
                      <Td><InlineCode>{device.device_id}</InlineCode></Td>
                      <Td>
                        <StatusChip variant={statusVariant(device.status)}>
                          {device.status}
                        </StatusChip>
                      </Td>
                      <Td>{timeAgo(device.last_seen)}</Td>
                    </tr>
                  ))}
                </tbody>
              </StyledTable>
            </TableContainer>
          )}
        </>
      ) : (
        /* Super-admin platform view: infra health, platform alerts, per-company breakdown */
        <>
          <ChartsRow>
            <DetailCard>
              <h2>Gateway Health</h2>
              {gateways.length === 0 ? (
                <EmptyState>
                  <p>No gateways registered</p>
                </EmptyState>
              ) : unhealthyGateways.length === 0 ? (
                <EmptyState>
                  <p>All {gateways.length} gateway{gateways.length > 1 ? 's' : ''} online.</p>
                </EmptyState>
              ) : (
                <FeedList>
                  {unhealthyGateways.map(gw => (
                    <FeedItem key={gw.id}>
                      <FeedMain>
                        <FeedTitle>{gw.name || gw.gateway_id}</FeedTitle>
                        <FeedMeta>
                          {gw.company_name || 'Unassigned'}
                          {gw.last_heartbeat ? ` · last seen ${timeAgo(gw.last_heartbeat)}` : ''}
                        </FeedMeta>
                      </FeedMain>
                      <StatusChip variant={gatewayVariant(gw.status)}>{gw.status}</StatusChip>
                    </FeedItem>
                  ))}
                </FeedList>
              )}
            </DetailCard>

            <DetailCard>
              <h2>Recent Alerts</h2>
              {recentAlerts.length === 0 ? (
                <EmptyState>
                  <p>No recent alerts</p>
                </EmptyState>
              ) : (
                <FeedList>
                  {recentAlerts.map(alert => (
                    <FeedItem key={alert.id}>
                      <FeedMain>
                        <FeedTitle>{alert.title || alert.message}</FeedTitle>
                        <FeedMeta>
                          {companyNameById.get(alert.company_id) || 'Unknown company'} · {timeAgo(alert.created_at)}
                        </FeedMeta>
                      </FeedMain>
                      <StatusChip variant={severityVariant(alert.severity)}>
                        {alert.severity}
                      </StatusChip>
                    </FeedItem>
                  ))}
                </FeedList>
              )}
            </DetailCard>
          </ChartsRow>

          <SectionHeader>
            <h2>Software Providers</h2>
            <QuickLink to="/software-providers">Manage Software Providers</QuickLink>
          </SectionHeader>

          {companyStatsLoading ? (
            <LoadingText>Loading...</LoadingText>
          ) : companyRows.length === 0 ? (
            <EmptyState>
              <p>No companies found</p>
            </EmptyState>
          ) : (
            <TableContainer>
              <StyledTable>
                <thead>
                  <tr>
                    <Th>Company</Th>
                    <NumHead>Devices</NumHead>
                    <Th>Fleet Health</Th>
                    <NumHead>Offline</NumHead>
                    <NumHead>MRR</NumHead>
                  </tr>
                </thead>
                <tbody>
                  {companyRows.map(c => (
                    <tr key={c.id}>
                      <Td>
                        <CompanyLink to={`/software-providers/${c.id}`}>{c.name}</CompanyLink>
                      </Td>
                      <NumCell>{c.total}</NumCell>
                      <Td>
                        <StatusChip variant={healthChipVariant(c.onlinePct)}>
                          {c.onlinePct === null ? 'No devices' : `${c.onlinePct}% online`}
                        </StatusChip>
                      </Td>
                      <NumCell>{c.offline}</NumCell>
                      <NumCell>{formatCurrency(c.mrr)}</NumCell>
                    </tr>
                  ))}
                </tbody>
              </StyledTable>
            </TableContainer>
          )}
        </>
      )}
    </PageContainer>
  );
}

export default Dashboard;
